import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { Formik, FormikProps, Form, FormikValues, Field, FieldProps } from 'formik';
import { FormControl, Divider } from '@material-ui/core';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader } from 'Components/Drawer';
import { SelectField } from 'Components/Form/MaterialForm';
import CloseIconButton from 'Components/Icons/CloseIconButton/CloseIconButton';
import TimePicker from 'Components/TimePicker/TimePicker';
import Button from 'Components/Button/Button';
import NotificationBanner from 'Components/NotificationBanner/NotificationBanner';
import { useCommonActions } from 'State/actions';
import * as Constants from 'Constants/Constants';
import { SidebarLocations } from 'Constants/Enums';
import { OrderingRules } from 'Interfaces/Interfaces';

import './OrderingRulesSidebar.less';

export interface OrderingRulesSidebarProps {
  open: boolean;
  orderingRules: OrderingRules;
  setSidebarOpen: (open: boolean) => void;
}
interface OrderingRulesValidationErrors {
  [field: string]: string;
}

const OrderingRulesSidebar: FC<OrderingRulesSidebarProps> = ({ open, orderingRules, setSidebarOpen }) => {
  const [loading, setLoading] = useState(false);
  const [orderRange, setOrderRange] = useState(
    orderingRules?.orderRange ?? Constants.ORDER_RANGE_OPTIONS.DAILY.toString(),
  );
  const [maxOrderRange, setMaxOrderRange] = useState(
    orderingRules?.maxOrderRange || Constants.MAX_ORDER_RANGE_OPTIONS.DAYS.toString(),
  );
  const [teacherMaxOrderRange, setTeacherMaxOrderRange] = useState(
    orderingRules?.teacherMaxOrderRange || Constants.MAX_ORDER_RANGE_OPTIONS.DAYS.toString(),
  );
  const [teacherOrderRange, setTeacherOrderRange] = useState(Constants.ORDER_RANGE_OPTIONS.DAILY.toString());
  const { updateDistrict } = useCommonActions();

  const initialValues = {
    orderRange: orderingRules?.orderRange || '',
    orderDueBy: orderingRules?.orderDueBy || '',
    orderDueByMonthly: orderingRules?.orderDueByMonthly || '',
    cutoffDay: orderingRules?.cutoffDay || '',
    cutoffTime: orderingRules?.cutoffTime || '',
    maxCutoffTime: orderingRules?.maxCutoffTime || '',
    maxOrderRange: orderingRules?.maxOrderRange || '',
    maxCutoffDays: orderingRules?.maxCutoffDays || '',
    maxCutoffWeeks: orderingRules?.maxCutoffWeeks || '',
    maxCutoffMonths: orderingRules?.maxCutoffMonths || '',
    teacherOrderRange: orderingRules?.teacherOrderRange || '',
    teacherOrderDueBy: orderingRules?.teacherOrderDueBy || '',
    teacherOrderDueByMonthly: orderingRules?.teacherOrderDueByMonthly || '',
    teacherCutoffDay: orderingRules?.teacherCutoffDay || '',
    teacherCutoffTime: orderingRules?.teacherCutoffTime || '',
    teacherMaxOrderRange: orderingRules?.teacherMaxOrderRange || '',
    teacherMaxCutoffDays: orderingRules?.teacherMaxCutoffDays || '',
    teacherMaxCutoffWeeks: orderingRules?.teacherMaxCutoffWeeks || '',
    teacherMaxCutoffMonths: orderingRules?.teacherMaxCutoffMonths || '',
  };
  const REQUIRED = 'This is required';

  const validateMinimumRules = (
    orderRangeField: string,
    cutoffDayField: string,
    orderDueByField: string,
    orderDueByMonthlyField: string,
    values: FormikValues,
    errors: OrderingRulesValidationErrors,
  ) => {
    const orderRange = values[orderRangeField];
    const cutoffDay = values[cutoffDayField];
    const orderDueBy = values[orderDueByField];
    const orderDueByMonthly = values[orderDueByMonthlyField];

    // Only validate if any field has actual content
    const hasContent = orderRange || cutoffDay || orderDueBy || orderDueByMonthly;
    if (!hasContent) return;

    if (!orderRange) {
      errors[orderRangeField] = REQUIRED;
      return;
    }

    switch (orderRange) {
      case Constants.ORDER_RANGE_OPTIONS.WEEKLY:
        if (!cutoffDay) {
          errors[cutoffDayField] = REQUIRED;
        }
        break;
      // DAILY and MONTHLY require no additional validation
    }
  };

  const validateMaximumRules = (
    maxOrderRangeField: string,
    maxCutoffDaysField: string,
    maxCutoffWeeksField: string,
    maxCutoffMonthsField: string,
    values: FormikValues,
    errors: OrderingRulesValidationErrors,
  ) => {
    const maxOrderRange = values[maxOrderRangeField];
    const maxCutoffDays = values[maxCutoffDaysField];
    const maxCutoffWeeks = values[maxCutoffWeeksField];
    const maxCutoffMonths = values[maxCutoffMonthsField];

    // Only validate if any field has actual content
    const hasContent = maxOrderRange || maxCutoffDays || maxCutoffWeeks || maxCutoffMonths;
    if (!hasContent) return;

    if (!maxOrderRange) {
      errors[maxOrderRangeField] = REQUIRED;
      return;
    }

    switch (maxOrderRange) {
      case Constants.MAX_ORDER_RANGE_OPTIONS.DAYS:
        if (!maxCutoffDays) {
          errors[maxCutoffDaysField] = REQUIRED;
        }
        break;
      case Constants.MAX_ORDER_RANGE_OPTIONS.WEEKS:
        if (!maxCutoffWeeks) {
          errors[maxCutoffWeeksField] = REQUIRED;
        }
        break;
      case Constants.MAX_ORDER_RANGE_OPTIONS.MONTHS:
        if (!maxCutoffMonths) {
          errors[maxCutoffMonthsField] = REQUIRED;
        }
        break;
    }
  };

  // Main validation function
  const validateCutoffRules = (values: FormikValues) => {
    const errors: OrderingRulesValidationErrors = {};

    // Parent Order Cutoff (Minimum) validation
    validateMinimumRules(
      Constants.ORDERING_RULES_KEYS.ORDER_RANGE,
      Constants.ORDERING_RULES_KEYS.CUTOFF_DAY,
      Constants.ORDERING_RULES_KEYS.ORDER_DUE_BY,
      Constants.ORDERING_RULES_KEYS.ORDER_DUE_BY_MONTHLY,
      values,
      errors,
    );

    // Teacher Order Cutoff (Minimum) validation
    validateMinimumRules(
      Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_RANGE,
      Constants.ORDERING_RULES_KEYS.TEACHER_CUTOFF_DAY,
      Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_DUE_BY,
      Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_DUE_BY_MONTHLY,
      values,
      errors,
    );

    // Parent Max Order Range validation
    validateMaximumRules(
      Constants.ORDERING_RULES_KEYS.MAX_ORDER_RANGE,
      Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_DAYS,
      Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_WEEKS,
      Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_MONTHS,
      values,
      errors,
    );

    // Teacher Max Order Range validation
    validateMaximumRules(
      Constants.ORDERING_RULES_KEYS.TEACHER_MAX_ORDER_RANGE,
      Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_DAYS,
      Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_WEEKS,
      Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_MONTHS,
      values,
      errors,
    );

    return errors;
  };

  const handleSubmit = async (values: FormikValues) => {
    setLoading(true);
    await updateDistrict({ orderingRules: { ...orderingRules, ...values } });
    setLoading(false);
    setSidebarOpen(false);
  };

  const handleChange = useCallback((field) => {
    if (field.name === Constants.ORDERING_RULES_KEYS.ORDER_RANGE) {
      setOrderRange(field.value);
    }
    if (field.name === Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_RANGE) {
      setTeacherOrderRange(field.value);
    }
    if (field.name === Constants.ORDERING_RULES_KEYS.MAX_ORDER_RANGE) {
      setMaxOrderRange(field.value);
    }
    if (field.name === Constants.ORDERING_RULES_KEYS.TEACHER_MAX_ORDER_RANGE) {
      setTeacherMaxOrderRange(field.value);
    }
  }, []);

  useEffect(() => {
    setOrderRange((orderingRules?.orderRange as string) || Constants.ORDER_RANGE_OPTIONS.DAILY.toString());
    setTeacherOrderRange(
      (orderingRules?.teacherOrderRange as string) || Constants.ORDER_RANGE_OPTIONS.DAILY.toString(),
    );
  }, [orderingRules]);

  const [
    orderRangeOptions,
    orderDueByOptions,
    cutoffDayOptions,
    cutoffMonthOptions,
    maxOrderRangeOptions,
    maxRangeDaysOptions,
    maxRangeWeeksOptions,
    maxRangeMonthsOptions,
  ] = useMemo(() => {
    const mapIdAndName = ({ id: value, name: text }: { id: string; name: string }) => ({ value, text });
    return [
      Object.values(Constants.ORDER_RANGES).map(mapIdAndName),
      Object.values(Constants.ORDER_DUE_BY_RULES).map(mapIdAndName),
      Object.values(Constants.ORDER_CUT_OFF_DAYS).map(mapIdAndName),
      Object.values(Constants.ORDER_DUE_BY_MONTH).map(mapIdAndName),
      Object.values(Constants.MAX_ORDER_RANGES).map(mapIdAndName),
      Object.values(Constants.MAX_ORDER_UP_TO_DAYS).map(mapIdAndName),
      Object.values(Constants.MAX_ORDER_UP_TO_WEEKS).map(mapIdAndName),
      Object.values(Constants.MAX_ORDER_UP_TO_MONTHS).map(mapIdAndName),
    ];
  }, []);

  return (
    <Drawer
      open={open}
      anchor={'right'}
      className="ordering-rules-sidebar-container"
      id="ordering-rules-sidebar-slideout"
    >
      <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize validate={validateCutoffRules}>
        {({ handleReset, setFieldValue, errors, touched }: FormikProps<FormikValues>) => {
          const typedErrors = errors as OrderingRulesValidationErrors;
          return (
            <Form className="linq-drawer-form-content oo-drawer-form">
              <DrawerHeader>
                <h2>Edit Order Cutoff and Max Range</h2>
                <CloseIconButton
                  onClick={() => {
                    setSidebarOpen(false);
                    handleReset();
                  }}
                />
              </DrawerHeader>
              <NotificationBanner location={SidebarLocations.ORDERING_RULES_SIDE_BAR} />
              <DrawerContent className="ordering-rules-sidebar-content">
                <section>
                  <h4 className="ordering-rules__section-header">Parent Order Cutoff</h4>
                  <p>Please choose time and cutoff.</p>
                  <div className="ordering-rules__section-option">
                    <Field name="orderRange">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth className="ordering-rules__section-range">
                          <SelectField
                            id="orderRange"
                            name="orderRange"
                            value={field.value}
                            fieldLabel="Order Range"
                            options={orderRangeOptions}
                            error={!!(typedErrors.orderRange && touched.orderRange)}
                            errorMsg={touched.orderRange ? typedErrors.orderRange : ''}
                            onChange={(e) => {
                              field.onChange(e);
                              handleChange(e.target);
                            }}
                          />
                        </FormControl>
                      )}
                    </Field>
                    {orderRange && orderRange === Constants.ORDER_RANGE_OPTIONS.DAILY && (
                      <Field name="orderDueBy">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth>
                            <SelectField
                              id="orderDueBy"
                              name="orderDueBy"
                              value={field.value}
                              fieldLabel="Order Due By"
                              options={orderDueByOptions}
                              error={!!(typedErrors.orderDueBy && touched.orderDueBy)}
                              errorMsg={touched.orderDueBy ? typedErrors.orderDueBy : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {orderRange && orderRange === Constants.ORDER_RANGE_OPTIONS.WEEKLY && (
                      <Field name="cutoffDay">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="cutoffDay"
                              name="cutoffDay"
                              value={field.value}
                              fieldLabel="Cut Off Day"
                              options={cutoffDayOptions}
                              error={!!(typedErrors.cutoffDay && touched.cutoffDay)}
                              errorMsg={touched.cutoffDay ? typedErrors.cutoffDay : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {orderRange && orderRange === Constants.ORDER_RANGE_OPTIONS.MONTHLY && (
                      <Field name="orderDueByMonthly">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="orderDueByMonthly"
                              name="orderDueByMonthly"
                              value={field.value}
                              fieldLabel="Order Due By"
                              options={cutoffMonthOptions}
                              error={!!(typedErrors.orderDueByMonthly && touched.orderDueByMonthly)}
                              errorMsg={touched.orderDueByMonthly ? typedErrors.orderDueByMonthly : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                  </div>

                  <p className="ordering-rules__section-cut-off">Cutoff Time</p>
                  <Field name="cutoffTime">
                    {({ field, form }: FieldProps) => (
                      <TimePicker
                        value={field.value}
                        onChange={(value: string) => {
                          form.setFieldValue(Constants.ORDERING_RULES_KEYS.CUTOFF_TIME, value);
                        }}
                      />
                    )}
                  </Field>
                  <h4 className="ordering-rules__section-header max-range">Parent Max Order Range</h4>
                  <p>Please choose how far in advance orders may be placed.</p>
                  <div className="ordering-rules__section-option">
                    <Field name="maxOrderRange">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth className="ordering-rules__section-range">
                          <SelectField
                            id="maxOrderRange"
                            name="maxOrderRange"
                            value={field.value}
                            fieldLabel="Max Order Range"
                            options={maxOrderRangeOptions}
                            error={!!(typedErrors.maxOrderRange && touched.maxOrderRange)}
                            errorMsg={touched.maxOrderRange ? typedErrors.maxOrderRange : ''}
                            onChange={(e) => {
                              field.onChange(e);
                              handleChange(e.target);
                            }}
                          />
                        </FormControl>
                      )}
                    </Field>
                    {maxOrderRange && maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.DAYS && (
                      <Field name="maxCutoffDays">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth>
                            <SelectField
                              id="maxCutoffDays"
                              name="maxCutoffDays"
                              value={field.value}
                              fieldLabel="Max Cutoff"
                              options={maxRangeDaysOptions}
                              error={!!(typedErrors.maxCutoffDays && touched.maxCutoffDays)}
                              errorMsg={touched.maxCutoffDays ? typedErrors.maxCutoffDays : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {maxOrderRange && maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.WEEKS && (
                      <Field name="maxCutoffWeeks">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="maxCutoffWeeks"
                              name="maxCutoffWeeks"
                              value={field.value}
                              fieldLabel="Max Cutoff"
                              options={maxRangeWeeksOptions}
                              error={!!(typedErrors.maxCutoffWeeks && touched.maxCutoffWeeks)}
                              errorMsg={touched.maxCutoffWeeks ? typedErrors.maxCutoffWeeks : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {maxOrderRange && maxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.MONTHS && (
                      <Field name="maxCutoffMonths">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="maxCutoffMonths"
                              name="maxCutoffMonths"
                              value={field.value}
                              fieldLabel="Max Cutoff"
                              options={maxRangeMonthsOptions}
                              error={!!(typedErrors.maxCutoffMonths && touched.maxCutoffMonths)}
                              errorMsg={touched.maxCutoffMonths ? typedErrors.maxCutoffMonths : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                  </div>

                  <div className="clear-btn-container">
                    <Button
                      color="primary"
                      variant="outlined"
                      type="button"
                      className="clear-min"
                      onClick={() => {
                        setFieldValue(Constants.ORDERING_RULES_KEYS.ORDER_RANGE, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.ORDER_DUE_BY, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.CUTOFF_DAY, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.CUTOFF_TIME, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.ORDER_DUE_BY_MONTHLY, '');
                      }}
                      data-test-id="clearParentBtn"
                    >
                      Clear Parent Cutoff
                    </Button>
                    <Button
                      color="primary"
                      variant="outlined"
                      type="button"
                      onClick={() => {
                        setFieldValue(Constants.ORDERING_RULES_KEYS.MAX_ORDER_RANGE, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_DAYS, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_WEEKS, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_MONTHS, '');
                        setFieldValue(Constants.ORDERING_RULES_KEYS.MAX_CUTOFF_TIME, '');
                      }}
                      data-test-id="clearMaxParentBtn"
                    >
                      Clear Parent Range
                    </Button>
                  </div>

                  <Divider />
                </section>
                <section>
                  <h4 className="ordering-rules__section-header">Teacher Order Cutoff</h4>
                  <p>Please choose the time and cutoff.</p>
                  <div className="ordering-rules__section-option">
                    <Field name="teacherOrderRange">
                      {({ field }: FieldProps) => (
                        <FormControl fullWidth className="ordering-rules__section-range">
                          <SelectField
                            id="teacherOrderRange"
                            name="teacherOrderRange"
                            value={field.value}
                            fieldLabel="Order Range"
                            options={orderRangeOptions}
                            error={!!(typedErrors.teacherOrderRange && touched.teacherOrderRange)}
                            errorMsg={touched.teacherOrderRange ? typedErrors.teacherOrderRange : ''}
                            onChange={(e) => {
                              field.onChange(e);
                              handleChange(e.target);
                            }}
                          />
                        </FormControl>
                      )}
                    </Field>
                    {teacherOrderRange && teacherOrderRange === Constants.ORDER_RANGE_OPTIONS.DAILY && (
                      <Field name="teacherOrderDueBy">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth>
                            <SelectField
                              id="teacherOrderDueBy"
                              name="teacherOrderDueBy"
                              value={field.value}
                              fieldLabel="Order Due By"
                              options={orderDueByOptions}
                              error={!!(typedErrors.teacherOrderDueBy && touched.teacherOrderDueBy)}
                              errorMsg={touched.teacherOrderDueBy ? typedErrors.teacherOrderDueBy : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {teacherOrderRange && teacherOrderRange === Constants.ORDER_RANGE_OPTIONS.WEEKLY && (
                      <Field name="teacherCutoffDay">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="teacherCutoffDay"
                              name="teacherCutoffDay"
                              value={field.value}
                              fieldLabel="Cutoff Day"
                              options={cutoffDayOptions}
                              error={!!(typedErrors.teacherCutoffDay && touched.teacherCutoffDay)}
                              errorMsg={touched.teacherCutoffDay ? typedErrors.teacherCutoffDay : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {teacherOrderRange && teacherOrderRange === Constants.ORDER_RANGE_OPTIONS.MONTHLY && (
                      <Field name="teacherOrderDueByMonthly">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-option">
                            <SelectField
                              id="teacherOrderDueByMonthly"
                              name="teacherOrderDueByMonthly"
                              value={field.value}
                              fieldLabel="Order Due by"
                              options={cutoffMonthOptions}
                              error={!!(typedErrors.teacherOrderDueByMonthly && touched.teacherOrderDueByMonthly)}
                              errorMsg={touched.teacherOrderDueByMonthly ? typedErrors.teacherOrderDueByMonthly : ''}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}
                  </div>
                  <div>
                    <p className="ordering-rules__section-cut-off">Cutoff Time</p>
                    <Field name="teacherCutoffTime">
                      {({ field, form }: FieldProps) => (
                        <TimePicker
                          value={field.value}
                          onChange={(value: string) => {
                            form.setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_CUTOFF_TIME, value);
                          }}
                        />
                      )}
                    </Field>

                    <h4 className="ordering-rules__section-header max-range">Teacher Max Order Range</h4>
                    <p>Please choose how far in advance orders may be placed.</p>
                    <div className="ordering-rules__section-option">
                      <Field name="teacherMaxOrderRange">
                        {({ field }: FieldProps) => (
                          <FormControl fullWidth className="ordering-rules__section-range">
                            <SelectField
                              id="teacherMaxOrderRange"
                              name="teacherMaxOrderRange"
                              value={field.value}
                              fieldLabel="Max Order Range"
                              options={maxOrderRangeOptions}
                              error={!!(typedErrors.teacherMaxOrderRange && touched.teacherMaxOrderRange)}
                              errorMsg={touched.teacherMaxOrderRange ? typedErrors.teacherMaxOrderRange : ''}
                              onChange={(e) => {
                                field.onChange(e);
                                handleChange(e.target);
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>
                      {teacherMaxOrderRange && teacherMaxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.DAYS && (
                        <Field name="teacherMaxCutoffDays">
                          {({ field }: FieldProps) => (
                            <FormControl fullWidth>
                              <SelectField
                                id="teacherMaxCutoffDays"
                                name="teacherMaxCutoffDays"
                                value={field.value}
                                fieldLabel="Max Cutoff"
                                options={maxRangeDaysOptions}
                                error={!!(typedErrors.teacherMaxCutoffDays && touched.teacherMaxCutoffDays)}
                                errorMsg={touched.teacherMaxCutoffDays ? typedErrors.teacherMaxCutoffDays : ''}
                                onChange={field.onChange}
                              />
                            </FormControl>
                          )}
                        </Field>
                      )}
                      {teacherMaxOrderRange && teacherMaxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.WEEKS && (
                        <Field name="teacherMaxCutoffWeeks">
                          {({ field }: FieldProps) => (
                            <FormControl fullWidth className="ordering-rules__section-option">
                              <SelectField
                                id="teacherMaxCutoffWeeks"
                                name="teacherMaxCutoffWeeks"
                                value={field.value}
                                fieldLabel="Max Cutoff"
                                options={maxRangeWeeksOptions}
                                error={!!(typedErrors.teacherMaxCutoffWeeks && touched.teacherMaxCutoffWeeks)}
                                errorMsg={touched.teacherMaxCutoffWeeks ? typedErrors.teacherMaxCutoffWeeks : ''}
                                onChange={field.onChange}
                              />
                            </FormControl>
                          )}
                        </Field>
                      )}
                      {teacherMaxOrderRange && teacherMaxOrderRange === Constants.MAX_ORDER_RANGE_OPTIONS.MONTHS && (
                        <Field name="teacherMaxCutoffMonths">
                          {({ field }: FieldProps) => (
                            <FormControl fullWidth className="ordering-rules__section-option">
                              <SelectField
                                id="teacherMaxCutoffMonths"
                                name="teacherMaxCutoffMonths"
                                value={field.value}
                                fieldLabel="Max Cutoff"
                                options={maxRangeMonthsOptions}
                                error={!!(typedErrors.teacherMaxCutoffMonths && touched.teacherMaxCutoffMonths)}
                                errorMsg={touched.teacherMaxCutoffMonths ? typedErrors.teacherMaxCutoffMonths : ''}
                                onChange={field.onChange}
                              />
                            </FormControl>
                          )}
                        </Field>
                      )}
                    </div>

                    <div className="clear-btn-container">
                      <Button
                        className="clear-min"
                        color="primary"
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_RANGE, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_DUE_BY, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_CUTOFF_DAY, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_CUTOFF_TIME, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_ORDER_DUE_BY_MONTHLY, '');
                        }}
                        data-test-id="clearTeacherBtn"
                      >
                        Clear Teacher Cutoff
                      </Button>
                      <Button
                        color="primary"
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_MAX_ORDER_RANGE, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_DAYS, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_WEEKS, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_MONTHS, '');
                          setFieldValue(Constants.ORDERING_RULES_KEYS.TEACHER_MAX_CUTOFF_TIME, '');
                        }}
                        data-test-id="clearMaxTeacherBtn"
                      >
                        Clear Teacher Range
                      </Button>
                    </div>
                  </div>
                </section>
              </DrawerContent>
              <DrawerFooter>
                <div>
                  <Button
                    className="linq-mr-2"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      setSidebarOpen(false);
                      handleReset();
                    }}
                    data-test-id="cancelBtn"
                  >
                    Cancel
                  </Button>
                </div>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  data-test-id="submitBtn"
                >
                  Save
                </Button>
              </DrawerFooter>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export default OrderingRulesSidebar;
